// Centralized CMS configuration
import appInsightsLogger from "../../features/logging/appInsightsLogger"
import { Content } from "./schema"

// Collection name mapping used across all loaders
export const COLLECTION_NAME_TO_REDUX_CONTENT_KEY_MAP: Record<
  string,
  keyof Content
> = {
  about: "about",
  advanced_search_page: "advancedSearchPage",
  affiliate_relations_actions: "affiliateRelationsActions",
  auth_and_releases: "authAndReleases",
  common_text: "common",
  contact_type_map: "contactTypeMap",
  contact_us: "contactUs",
  content_layout: "contentLayout",
  faq: "faq",
  faq_page: "faqPage",
  government_type_map: "governmentTaxTypeMap",
  irs_statuses: "irsStatuses",
  liability: "liability",
  liability_status_map: "liabilityStatusMap",
  location_address: "locationAddress",
  location_list_item: "locationListItem",
  location_list_screen: "locationListScreen",
  member_intake: "memberIntake",
  navigation: "navigation",
  office_initial_setup: "officeInitialSetup",
  office_requirements: "officeRequirements",
  office_type_map: "officeTypeMap",
  onboarding_page: "onboarding",
  popover_menu_children: "popoverMenuChildren",
  payments: "payments",
  payment_status_map: "paymentStatusMap",
  practice: "practice",
  practice_address: "practiceAddress",
  practice_agreement: "practiceAgreement",
  practice_contact: "practiceContact",
  practice_details: "practiceDetails",
  practice_header: "practiceHeader",
  practice_nav_menu: "practiceNavMenu",
  practice_profile_process: "practiceProfileProcess",
  practice_rates: "practiceRates",
  practice_sub_nav: "practiceSubNav",
  practice_tags_map: "practiceTagsMap",
  privacy_policy: "privacyPolicy",
  practice_individuals: "practiceIndividuals",
  practice_invitations: "practiceInvitations",
  provider: "provider",
  provider_ages_seen: "providerAgesSeen",
  provider_client_ethnicity: "providerClientEthnicity",
  provider_communities_served_map: "providerCommunitiesServedMap",
  provider_qualifications_license_type_map:
    "providerQualificationsLicenseTypeMap",
  provider_telehealth_platform_map: "providerTelehealthPlatformMap",
  provider_therapy_type_map: "providerTherapyTypeMap",
  provider_designations_map: "providerDesignationsMap",
  provider_languages_map: "providerLanguagesMap",
  provider_license_status_map: "providerLicenseStatusMap",
  provider_session_type: "providerSessionType",
  provider_specialty_map: "providerSpecialtiesMap",
  provider_title_dropdown_map: "providerTitleDropdownMap",
  provider_gender: "providerGenderMap",
  provider_insurance_carriers: "providerInsuranceCarriers",
  provider_military_status: "providerMilitaryStatusMap",
  provider_participant: "providerParticipant",
  provider_ethnic_group: "providerEthnicGroupMap",
  provider_faith_orientation: "providerFaithOrientation",
  provider_qualifications_credential_type_map:
    "providerQualificationsCredentialTypeMap",
  provider_qualifications_education_degree_type_map:
    "providerQualificationsDegreeTypeMap",
  provider_religions: "providerReligionsMap",
  provider_sexual_orientation: "providerSexualOrientationMap",
  provider_status_verbs: "providerStatusVerbs",
  provider_status_adjectives: "providerStatusAdjectives",
  provider_tags_map: "providerTagsMap",
  public_profile_slide: "publicProfileSlide",
  referral_page: "referralPage",
  referral_packet_modal: "referralPacketModal",
  referral_session_status_map: "referralSessionStatusMap",
  request_referral: "requestReferralModal",
  referrer_code_map: "referrerCodeMap",
  search: "search",
  taskboard: "taskboard",
  tax_information: "taxInformation",
  tax_organization_map: "taxOrganizationTypeMap",
  terms_of_use: "termsOfUseAccept",
  terms_of_use_page: "termsOfUsePage",
  transfer_reason_map: "transferReasonMap",
  user_roles: "userRoles",
  with_authentication: "withAuthentication",
  virtual_location: "virtualLocation",
  system_messages: "systemMessages",
}

// Derive collection names array from the mapping
export const COLLECTION_NAMES = Object.keys(
  COLLECTION_NAME_TO_REDUX_CONTENT_KEY_MAP,
)

// TODO: update this to not be hard coded once translations are added to the cms
// IMPORTANT: once this is extended, we also need to update the country filtering in useLocationFilter.ts
//            it might be worth it to centralize it in a single file when we get there
export const LANGUAGE_CODE = "en-US"

// CMS base paths
const BASE_CMS_BASE = "/base" // base/fallback file content from build
const LIVE_CMS_BASE = "/live" // live file content from redis

let cmsMode: "base" | "live" | null = null // Cache the determined mode

/**
 * Check if live CMS content is available
 */
export const isLiveCMSAvailable = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${LIVE_CMS_BASE}/cms-manifest.json`, {
      method: "GET",
      cache: "no-cache",
    })

    if (!response.ok) {
      return false
    }

    // Validate that we're getting actual JSON, not HTML
    const contentType = response.headers.get("content-type")
    if (!contentType || !contentType.includes("application/json")) {
      appInsightsLogger.traceInfo(
        "Live CMS manifest returned non-JSON content type:",
        contentType,
      )
      return false
    }

    // Validate the JSON structure contains expected manifest fields
    const manifest = await response.json()
    if (!manifest.contentVersion || !manifest.collections || !manifest.files) {
      appInsightsLogger.traceInfo(
        "Live CMS manifest missing required fields:",
        manifest,
      )
      return false
    }

    appInsightsLogger.traceInfo(
      `Live CMS content is available (version: ${manifest.contentVersion})`,
    )
    return true
  } catch (error) {
    appInsightsLogger.traceInfo(
      "Live CMS content not available, falling back to static:",
      error,
    )
  }
  return false
}

/**
 * Initialize CMS mode based live content availability
 */
export const initializeCMSMode = async (): Promise<void> => {
  if (await isLiveCMSAvailable()) {
    cmsMode = "live"
    appInsightsLogger.traceInfo("CMS mode: live (available)")
  } else {
    cmsMode = "base"
    appInsightsLogger.traceInfo("CMS mode: base (live not available)")
  }
}

/**
 * Get the CMS base path - uses cached mode to avoid repeated checks
 */
export const getCMSBasePath = (): string => {
  if (cmsMode === null) {
    appInsightsLogger.traceWarning("CMS mode not initialized")
    return BASE_CMS_BASE
  }

  return cmsMode === "live" ? LIVE_CMS_BASE : BASE_CMS_BASE
}

/**
 * Get collection path
 */
export const getCollectionPath = (): string => {
  return getCMSBasePath() + "/collectionData"
}

/**
 * Get files path
 */
export const getFilesPath = (): string => {
  return getCMSBasePath() + "/files"
}

/**
 * Reset CMS mode (for testing or re-initialization)
 */
export const resetCMSMode = (): void => {
  cmsMode = null
}
